// Self-hosted fonts (no external requests)
import '@fontsource-variable/inter/index.css'
import '@fontsource-variable/manrope/index.css'
import '@/assets/fonts/material-symbols-rounded.css'
// element css
import 'element-plus/dist/index.css'
// element dark css
import 'element-plus/theme-chalk/dark/css-vars.css'
import '@/styles/app.scss'

import axios from 'axios'
import ElementPlus from 'element-plus'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createApp } from 'vue'
import VueCookies from 'vue3-cookies'

import type { GlobalSettingsPublic } from '@/apis/interface/globalSettings'
import { OAUTH_CALLBACK_PATH } from '@/constants'
import globalFilter from '@/filters/index'
import { i18n, KEY_MISSING_TRANSLATIONS } from '@/langs'
import { createAppRouter } from '@/routers/index'
import { useConfigStore } from '@/stores/configStore'
import type { Config } from '@/stores/interface/configStore.d'

import App from './App.vue'
import PermissionDirective from './directives/permission'

/**
 * Entra (and other IdPs) redirect to a path-only URI, e.g. /oauth-callback/microsoft?code=...
 * This app uses Vue Router hash history, so the active route is read from location.hash, not
 * pathname. Without this step, the shell loads on #/ while code/state sit on the pathname and
 * OAuthCallbackPage never runs. Rewrite once to /#/oauth-callback/...?...
 */
const redirectPathOAuthCallbackToHash = (): boolean => {
  const { pathname, search, hash, origin } = window.location
  if (!pathname.startsWith(`${OAUTH_CALLBACK_PATH}/`)) return false
  const q = search || ''
  if (!q.includes('code=') && !q.includes('error=')) return false
  if (hash.startsWith(`#${OAUTH_CALLBACK_PATH}/`)) return false
  window.location.replace(`${origin}/#${pathname}${q}`)
  return true
}

const pickStr = (v: string | null | undefined): string | undefined => {
  if (v == null) return undefined
  const s = String(v).trim()
  return s || undefined
}

const mergeGlobalSettingsPublic = async (cfg: Config): Promise<Config> => {
  const merged: Config = { ...cfg }
  try {
    const base = cfg.apiBaseUrl.replace(/\/?$/, '')
    const { data } = await axios.get<GlobalSettingsPublic>(`${base}/global-settings/public`)
    const cn = pickStr(data.companyName ?? undefined)
    if (cn) merged.companyName = cn
    const fav = pickStr(data.faviconUrl ?? undefined)
    if (fav) merged.faviconUrl = fav
    const lm = pickStr(data.logoMainUrl ?? undefined)
    if (lm) merged.logoMainUrl = lm
    const ls = pickStr(data.logoSmallUrl ?? undefined)
    if (ls) merged.logoSmallUrl = ls
    const css = pickStr(data.globalCss ?? undefined)
    if (css) merged.globalSettingsCss = css
    else merged.globalSettingsCss = undefined
  } catch (e) {
    console.warn('Could not load /global-settings/public', e)
  }
  return merged
}

const setup = () => {
  if (redirectPathOAuthCallbackToHash()) {
    return
  }
  const router = createAppRouter()
  const app = createApp(App)

  app.use(ElementPlus, {})
  app.use(i18n)
  localStorage.removeItem(KEY_MISSING_TRANSLATIONS)
  app.use(VueCookies)

  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)
  app.use(pinia)

  app.use(router)

  app.config.globalProperties.$filters = globalFilter

  app.directive('permission', PermissionDirective)

  app.mount('#app')

  initApp()
}

const initApp = () => {
  const configStore = useConfigStore()

  const faviconUrl = configStore.getterCurrentConfig?.faviconUrl
  const favicon = document.getElementById('favicon') as HTMLLinkElement | null

  if (faviconUrl && favicon) {
    favicon.href = faviconUrl
    const lower = faviconUrl.split('?')[0]?.toLowerCase() ?? ''
    if (lower.endsWith('.png')) favicon.type = 'image/png'
    else if (lower.endsWith('.svg')) favicon.type = 'image/svg+xml'
    else if (lower.endsWith('.ico')) favicon.type = 'image/x-icon'
  }
}

axios.get<Config>('config.json').then(async resp => {
  console.log('Application config loaded.', resp.data)

  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  const app = createApp({})
  app.use(pinia)

  const configStore = useConfigStore()
  const merged = await mergeGlobalSettingsPublic(resp.data)
  configStore.setCurrentConfig(merged).then(() => {
    setup()
  })
})
